import { SelectFormControl, type SelectFormControlProps } from 'BrokerTsLib/src/components'
import { useFormContext } from 'BrokerTsLib/src/rhf'

import { useGetOrganizationUnits, useGetUsers } from '@/hooks/useUser'
import { useTranslationsContext } from 'BrokerTsLib/src/context/TranslationsContext'

interface OrganizationUserFormControlsProps {
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
  variant?: SelectFormControlProps['variant']
  selectStyle?: SelectFormControlProps['sx']
}

/** A component which renders FormControls of OrganizationUnit and User with cross filtering. */
export const OrganizationUserFormControls = ({ showLabel = true, size = 'lg', variant, selectStyle }: OrganizationUserFormControlsProps) => {
  const { setValue, watch, control } = useFormContext()
  const { translate } = useTranslationsContext()

  const ouId = watch('ouId') as number
  const userId = watch('userId') as string

  const { data: organizationUnits, isFetching: isFetchingOUs } = useGetOrganizationUnits()

  const { data: users, isFetching: isFetchingUsers } = useGetUsers(ouId)
  
  const onOuIdChanged = (newOuId: number) => {
    if (newOuId && users && !users?.data.some((x) => x.userId === userId))
      setValue('userId', '')
  }

  if ((organizationUnits?.data.length ?? 0) === 0) return null

  return (
    <>
      <SelectFormControl name='ouId' control={control} valueAsNumber
        label={showLabel ? translate('organizationUnit', 'Organizační jednotka') : undefined}
        options={
          organizationUnits?.data.map((ou) => ({
            value: ou.id.toString(),
            label: `${ou.name} ${ou.description}`,
          }))
        }
        unselectedText={translate('selectOrganization', 'Vyberte organizaci')}
        onChange={(v) => onOuIdChanged(v as number)}
        isLoading={isFetchingOUs}
        size={size}
        variant={variant}
        sx={selectStyle}
      />

      <SelectFormControl name='userId' control={control}
        label={showLabel ? translate('user', 'Uživatel') : undefined}
        options={
          users?.data.map((u) => ({
            value: u.userId!,
            label: `${u.surname} ${u.name} (${u.ouName}) ${u.ouDescription ?? ''}`,
          }))
        }
        unselectedText={translate('selectUser', 'Vyberte uživatele')}
        isLoading={isFetchingUsers}
        size={size}
        variant={variant}
        sx={selectStyle}
      />
    </>
  )
}
